import * as React from 'react'
import { User, Heart, CreditCard, Settings, Check, Info, Trash2, Plus, X } from 'lucide-react'
import { ExpandableDialog } from '../molecules/expandable-dialog'
import { Button } from '../../components/button'
import { Input } from '../../components/input'
import { Label } from '../../components/label'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/card'
import { Badge } from '../../components/badge'
import { Switch } from '../atoms/switch'
import { Separator } from '../../components/separator'
import { Textarea } from '../../components/textarea'
import { Checkbox } from '../../components/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/select'
import { showToast } from '../../components/toast'
import { useMetricPreferences, MetricPreferencesProvider } from '../../contexts/metric-preferences-context'
import { AddPaymentMethodDialog } from './add-payment-method-dialog'
import { calculateBMI } from '../../lib/form-validation'

// JoinOmu Logo Component
function JoinOmuLogo({ className }: { className?: string }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M12 18C12 21.3137 9.31371 24 6 24C2.68629 24 0 21.3137 0 18C0 14.6863 2.68629 12 6 12C9.31371 12 12 14.6863 12 18Z" fill="url(#paint0_linear_202_550)"/>
      <path d="M6 3C6 1.34315 7.34315 0 9 0C10.6569 0 12 1.34315 12 3V12H6V3Z" fill="url(#paint1_linear_202_550)"/>
      <path d="M12 3C12 1.34315 13.3431 0 15 0C16.6569 0 18 1.34315 18 3V12H12V3Z" fill="url(#paint2_linear_202_550)"/>
      <path d="M12 12H24V18C24 21.3137 21.3137 24 18 24C14.6863 24 12 21.3137 12 18V12Z" fill="url(#paint3_linear_202_550)"/>
      <defs>
        <linearGradient id="paint0_linear_202_550" x1="13.1184" y1="12" x2="22.8816" y2="24" gradientUnits="userSpaceOnUse">
          <stop stopColor="#BBDDFF"/>
          <stop offset="1" stopColor="#C85A15"/>
        </linearGradient>
        <linearGradient id="paint1_linear_202_550" x1="13.1184" y1="12" x2="22.8816" y2="24" gradientUnits="userSpaceOnUse">
          <stop stopColor="#BBDDFF"/>
          <stop offset="1" stopColor="#C85A15"/>
        </linearGradient>
        <linearGradient id="paint2_linear_202_550" x1="13.1184" y1="12" x2="22.8816" y2="24" gradientUnits="userSpaceOnUse">
          <stop stopColor="#BBDDFF"/>
          <stop offset="1" stopColor="#C85A15"/>
        </linearGradient>
        <linearGradient id="paint3_linear_202_550" x1="13.1184" y1="12" x2="22.8816" y2="24" gradientUnits="userSpaceOnUse">
          <stop stopColor="#BBDDFF"/>
          <stop offset="1" stopColor="#C85A15"/>
        </linearGradient>
      </defs>
    </svg>
  )
}

export interface AccountDialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  activeSection?: string
  onSectionChange?: (section: string) => void
  onAddressChange?: () => void | Promise<void>
  onSupportClick?: () => void
}

// Import auth service (will be dynamically imported to avoid build issues in Storybook)
let authService: any = null
const getAuthService = async () => {
  if (!authService) {
    try {
      const module = await import('@joinomu/shared')
      authService = module.authService
    } catch (error) {
      console.warn('Auth service not available (likely in Storybook):', error)
      // Return mock service for Storybook
      authService = {
        getCurrentUser: () => ({ user: null, error: null }),
        getUserProfile: () => ({ data: null, error: null }),
        updatePatientProfile: () => ({ success: true, error: null }),
        updatePassword: () => ({ success: true, error: null })
      }
    }
  }
  return authService
}

const sidebarItems = [
  {
    id: 'account',
    name: 'Account',
    icon: User
  },
  {
    id: 'health-profile',
    name: 'Health Profile',
    icon: Heart
  },
  {
    id: 'billing-and-plans',
    name: 'Billing and Plans',
    icon: CreditCard
  },
  {
    id: 'preferences',
    name: 'Preferences',
    icon: Settings
  }
]

// Create a wrapper component for preferences that includes the provider
function PreferencesWithProvider({ 
  currentSection,
  isSaving,
  hasPreferencesChanges,
  setHasPreferencesChanges,
  setError,
  setIsSaving,
  handleCancelPreferences,
  handleSavePreferences
}: {
  currentSection: string
  isSaving: boolean
  hasPreferencesChanges: boolean
  setHasPreferencesChanges: (value: boolean) => void
  setError: (error: string | null) => void
  setIsSaving: (value: boolean) => void
  handleCancelPreferences: () => void
  handleSavePreferences: () => void
}) {
  const { preferences, isLoading: prefsLoading, updatePreference } = useMetricPreferences()
  const [localPreferences, setLocalPreferences] = React.useState(preferences)
  
  // Sync local preferences with context when preferences change
  React.useEffect(() => {
    setLocalPreferences(preferences)
  }, [preferences])

  const handleLocalPreferenceChange = (key: string, enabled: boolean) => {
    setLocalPreferences(prev => 
      prev.map(pref => 
        pref.key === key ? { ...pref, enabled } : pref
      )
    )
    setHasPreferencesChanges(true)
  }

  const handleSavePrefs = async () => {
    if (!hasPreferencesChanges) return

    setIsSaving(true)
    setError(null)

    try {
      // Save all changed metric preferences
      for (const localPref of localPreferences) {
        const originalPref = preferences.find(p => p.key === localPref.key)
        if (originalPref && originalPref.enabled !== localPref.enabled) {
          await updatePreference(localPref.key, localPref.enabled)
        }
      }

      setHasPreferencesChanges(false)
      showToast({
        title: "Preferences saved",
        description: "Your metric preferences have been updated successfully."
      })
    } catch (error) {
      console.error('Error saving preferences:', error)
      setError('Failed to save preferences. Please try again.')
      showToast({
        title: "Error saving preferences",
        description: "Please try again later.",
        variant: "error"
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelPrefs = () => {
    // Revert local preferences to original state
    setLocalPreferences(preferences)
    setHasPreferencesChanges(false)
    setError(null)
  }

  const getMetricDescription = (key: string) => {
    const descriptions: Record<string, string> = {
      'weight': 'Show weight data in charts',
      'steps': 'Show daily step count',
      'sleep': 'Show sleep tracking data',
      'calories': 'Show calorie intake and burn data',
      'protein': 'Show protein intake tracking',
      'sugar': 'Show sugar intake monitoring',
      'water': 'Show daily water intake',
      'heart-rate': 'Show heart rate data and trends'
    }
    return descriptions[key] || 'Track this metric'
  }

  return (
    <div className="space-y-6">
      {/* Tracking Chart Preferences */}
      <Card>
        <CardHeader>
          <CardTitle>Tracking Chart Preferences</CardTitle>
          <p className="text-sm text-muted-foreground">
            Control which metrics appear in your tracking charts and add metrics dialog
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {prefsLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading preferences...</div>
          ) : (
            localPreferences.map((preference, index) => (
              <React.Fragment key={preference.key}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{preference.name}</p>
                    <p className="text-sm text-muted-foreground">{getMetricDescription(preference.key)}</p>
                  </div>
                  <Switch 
                    checked={preference.enabled}
                    onCheckedChange={(enabled) => {
                      handleLocalPreferenceChange(preference.key, enabled)
                    }}
                    disabled={isSaving}
                  />
                </div>
                {index < localPreferences.length - 1 && <Separator />}
              </React.Fragment>
            ))
          )}
        </CardContent>
      </Card>

      {/* Connected Wearables */}
      <Card style={{ display: 'none' }}>
        <CardHeader>
          <CardTitle>Connected Wearables</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 border rounded-lg bg-green-50">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-black rounded text-white text-xs flex items-center justify-center">A</div>
              <div>
                <p className="font-medium">Apple Watch Series 9</p>
                <p className="text-sm text-muted-foreground">Connected</p>
              </div>
            </div>
            <Button variant="destructive" size="sm">Disconnect</Button>
          </div>
          
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded text-white text-xs flex items-center justify-center">F</div>
              <div>
                <p className="font-medium">Fitbit Versa 4</p>
                <p className="text-sm text-muted-foreground">Not connected</p>
              </div>
            </div>
            <Button variant="outline" size="sm">Connect</Button>
          </div>
          
          <Button variant="outline" className="w-full">Add New Device</Button>
        </CardContent>
      </Card>

      {/* Provider Access */}
      <Card>
        <CardHeader>
          <CardTitle>Provider Access</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Share Tracking Data</p>
              <p className="text-sm text-muted-foreground">Allow your provider to view your health tracking data</p>
            </div>
            <Switch defaultChecked onCheckedChange={() => setHasPreferencesChanges(true)} />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end space-x-2">
        <Button
          variant="outline"
          disabled={isSaving}
          onClick={handleCancelPrefs}
        >
          Cancel
        </Button>
        <Button
          disabled={!hasPreferencesChanges || isSaving}
          onClick={handleSavePrefs}
        >
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  )
}

export function AccountDialog({
  open = true,
  onOpenChange,
  activeSection = 'Account',
  onSectionChange,
  onAddressChange,
  onSupportClick
}: AccountDialogProps) {
  const [currentSection, setCurrentSection] = React.useState(activeSection)
  const [hasAccountChanges, setHasAccountChanges] = React.useState(false)
  const [hasHealthChanges, setHasHealthChanges] = React.useState(false)
  const [hasPreferencesChanges, setHasPreferencesChanges] = React.useState(false)
  const [hasAddressChanges, setHasAddressChanges] = React.useState(false)

  // User profile data state
  const [userProfile, setUserProfile] = React.useState<any>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // Form data state
  const [formData, setFormData] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: ''
  })

  // Health profile data state
  const [healthData, setHealthData] = React.useState({
    heightFeet: '',
    heightInches: '',
    weight: '',
    bmi: '',
    allergies: [] as string[]
  })

  // Patient addresses state
  const [patientAddresses, setPatientAddresses] = React.useState<any[]>([])
  const [isLoadingAddresses, setIsLoadingAddresses] = React.useState(false)
  const [addressError, setAddressError] = React.useState<string | null>(null)
  const [forceRefreshAddresses, setForceRefreshAddresses] = React.useState(0)

  // Payment history state
  const [paymentHistory, setPaymentHistory] = React.useState<any[]>([])
  const [isLoadingPaymentHistory, setIsLoadingPaymentHistory] = React.useState(false)
  const [paymentHistoryError, setPaymentHistoryError] = React.useState<string | null>(null)
  
  // Upcoming invoice state
  const [upcomingInvoice, setUpcomingInvoice] = React.useState<any>(null)
  const [isLoadingUpcomingInvoice, setIsLoadingUpcomingInvoice] = React.useState(false)
  const [upcomingInvoiceError, setUpcomingInvoiceError] = React.useState<string | null>(null)

  // Assigned provider email state
  const [providerEmail, setProviderEmail] = React.useState<{email: string; providerName: string} | null | undefined>(undefined)
  const [isLoadingProviderEmail, setIsLoadingProviderEmail] = React.useState(false)
  // Payment methods state
  const [paymentMethods, setPaymentMethods] = React.useState<any[]>([])
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = React.useState(false)
  const [paymentMethodsError, setPaymentMethodsError] = React.useState<string | null>(null)
  const [addPaymentMethodOpen, setAddPaymentMethodOpen] = React.useState(false)

  // Removed medication subscriptions state - no longer needed

  // Add address form state
  const [newAddressData, setNewAddressData] = React.useState({
    streetLine1: '',
    streetLine2: '',
    city: '',
    state: '',
    zipCode: '',
    isPrimary: false
  })


  // Update current section when activeSection prop changes
  React.useEffect(() => {
    setCurrentSection(activeSection)
  }, [activeSection])

  // Load user profile data when dialog opens (with caching to prevent flashing)
  React.useEffect(() => {
    if (open) {
      // Only load if we don't already have data or if we explicitly need to refresh
      if (!userProfile || userProfile === null) {
        loadUserProfile()
      }
      if ((patientAddresses.length === 0 || forceRefreshAddresses > 0) && !isLoadingAddresses) {
        loadPatientAddresses()
      }
      if (paymentHistory.length === 0 && !isLoadingPaymentHistory) {
        loadPaymentHistory()
      }
      if (providerEmail === undefined && !isLoadingProviderEmail) {
        loadProviderEmail()
      }
      // Payment methods and upcoming invoice will be loaded after profile data is available
    }
  }, [open, userProfile, patientAddresses.length, isLoadingAddresses, forceRefreshAddresses, paymentHistory.length, isLoadingPaymentHistory, providerEmail, isLoadingProviderEmail])

  // Load payment methods and upcoming invoice after profile data is available
  React.useEffect(() => {
    if (open && userProfile?.id) {
      loadPaymentMethods()
      loadUpcomingInvoice()
    }
  }, [open, userProfile?.id])

  // Force refresh addresses when needed (after save/delete operations)
  React.useEffect(() => {
    if (forceRefreshAddresses > 0) {
      console.log('🔄 Force refreshing addresses due to data change')
      loadPatientAddresses()
      setForceRefreshAddresses(0) // Reset force refresh flag
    }
  }, [forceRefreshAddresses])

  const loadUserProfile = async () => {
    setIsLoading(true)
    setError(null)
    try {
      console.log('🔍 AccountDialog: Starting to load user profile...')
      const auth = await getAuthService()
      console.log('🔍 AccountDialog: Got auth service:', !!auth)
      const { data, error } = await auth.getUserProfile()
      console.log('🔍 AccountDialog: Profile result:', { data: !!data, error: !!error })
      
      if (error) {
        setError('Failed to load profile data')
        console.error('❌ AccountDialog: Profile load error:', error)
      } else if (data) {
        console.log('✅ AccountDialog: Profile data loaded successfully:', data)
        setUserProfile(data)
        setFormData({
          firstName: data.first_name || '',
          lastName: data.last_name || '',
          email: data.email || '',
          phone: data.phone || '',
          password: ''
        })
        // Extract health data from intake_data if available, fallback to direct fields
        const intakeData = data.intake_data || {}
        setHealthData({
          heightFeet: intakeData.height_feet || data.height_feet || '',
          heightInches: intakeData.height_inches || data.height_inches || '',
          weight: intakeData.weight || data.weight || '',
          bmi: intakeData.bmi || data.bmi || '',
          allergies: Array.isArray(intakeData.allergies) 
            ? intakeData.allergies 
            : Array.isArray(data.allergies) 
              ? data.allergies 
              : (intakeData.allergies ? [intakeData.allergies] : data.allergies ? [data.allergies] : [])
        })
        // Reset changes state when loading fresh data
        setHasAccountChanges(false)
        setHasHealthChanges(false)
      } else {
        console.log('⚠️ AccountDialog: No profile data returned')
        setError('No profile data found')
      }
    } catch (err) {
      setError('Failed to load profile data')
      console.error('❌ AccountDialog: Profile load exception:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const loadPatientAddresses = async () => {
    setIsLoadingAddresses(true)
    setAddressError(null)
    try {
      console.log('🔍 AccountDialog: Loading patient addresses...')
      const auth = await getAuthService()
      const { data, error } = await auth.getPatientAddresses()
      
      if (error) {
        setAddressError('Failed to load addresses')
        console.error('❌ AccountDialog: Address load error:', error)
      } else {
        console.log('✅ AccountDialog: Addresses loaded successfully:', data)
        setPatientAddresses(data || [])
      }
    } catch (err) {
      setAddressError('Failed to load addresses')
      console.error('❌ AccountDialog: Address load exception:', err)
    } finally {
      setIsLoadingAddresses(false)
    }
  }

  const loadProviderEmail = async () => {
    setIsLoadingProviderEmail(true)
    
    // Set a timeout to prevent infinite loading state
    const timeoutId = setTimeout(() => {
      console.log('⏰ AccountDialog: Provider email loading timeout - setting to no provider assigned')
      setProviderEmail(null)
      setIsLoadingProviderEmail(false)
    }, 10000) // 10 second timeout
    
    try {
      console.log('🔍 AccountDialog: Loading assigned provider email...')
      const auth = await getAuthService()
      const { data, error } = await auth.getAssignedProviderEmail()

      // Clear timeout since we got a response
      clearTimeout(timeoutId)

      if (error) {
        console.error('❌ AccountDialog: Provider email load error:', error)
        // Check if it's a 404 error (patient not found) vs other errors
        if (error.message?.includes('Patient not found') || error.message?.includes('404') || error.code === 'PGRST116') {
          console.log('ℹ️ AccountDialog: Patient record not found - treating as no provider assigned')
          setProviderEmail(null)
        } else {
          console.log('⚠️ AccountDialog: Other error occurred, treating as no provider assigned')
          setProviderEmail(null)
        }
      } else if (data) {
        console.log('✅ AccountDialog: Provider email loaded successfully:', data)
        setProviderEmail(data)
      } else {
        console.log('ℹ️ AccountDialog: No provider assigned to patient')
        setProviderEmail(null)
      }
    } catch (err) {
      console.error('❌ AccountDialog: Provider email load exception:', err)
      // Clear timeout and set to null on exception to prevent infinite loading
      clearTimeout(timeoutId)
      setProviderEmail(null)
    } finally {
      setIsLoadingProviderEmail(false)
    }
  }

  const loadPaymentHistory = async () => {
    setIsLoadingPaymentHistory(true)
    setPaymentHistoryError(null)
    try {
      console.log('🔍 AccountDialog: Loading payment history...')
      const auth = await getAuthService()

      // Get current user to get patient ID
      const { user } = await auth.getCurrentUser()
      if (!user?.id) {
        throw new Error('User not authenticated')
      }

      const { data, error } = await auth.getUserProfile()
      if (error || !data) {
        throw new Error('Failed to get user profile')
      }

      // Use the StripeService to get payment history
      const module = await import('@joinomu/shared')
      const result = await module.StripeService.getPaymentHistory(data.id)
      
      if (result.error) {
        setPaymentHistoryError('Failed to load payment history')
        console.error('❌ AccountDialog: Payment history load error:', result.error)
      } else {
        console.log('✅ AccountDialog: Payment history loaded successfully:', result.data?.length, 'payments')
        console.log('📄 PAYMENT HISTORY DEBUG: Raw payment data:', result.data)
        
        // Enhanced debug logging for payment history
        if (result.data && result.data.length > 0) {
          result.data.forEach((payment, index) => {
            console.log(`📄 PAYMENT ${index + 1} DEBUG:`, {
              id: payment.id,
              type: payment.type,
              amount: payment.amount,
              currency: payment.currency,
              description: payment.description,
              status: payment.status,
              created: payment.created,
              hasInvoiceUrl: !!payment.hosted_invoice_url,
              hasReceiptUrl: !!payment.receipt_url
            })
          })
        }
        
        setPaymentHistory(result.data || [])
      }
    } catch (err) {
      setPaymentHistoryError('Failed to load payment history')
      console.error('❌ AccountDialog: Payment history load exception:', err)
    } finally {
      setIsLoadingPaymentHistory(false)
    }
  }

  const loadPaymentMethods = async () => {
    setIsLoadingPaymentMethods(true)
    setPaymentMethodsError(null)
    try {
      console.log('🔍 AccountDialog: Loading payment methods...')
      
      // Only try to load payment methods if we have profile data already
      if (!userProfile?.id) {
        console.log('🔍 AccountDialog: No patient profile data available yet, skipping payment methods')
        return
      }
      
      // Use the StripeService to get payment methods
      const module = await import('@joinomu/shared')
      const result = await module.StripeService.getPaymentMethods(userProfile.id)
      
      if (result.error) {
        console.warn('⚠️ AccountDialog: Payment methods not available:', result.error)
        // Don't set error state for API unavailability in local dev
        setPaymentMethods([])
      } else {
        console.log('✅ AccountDialog: Payment methods loaded successfully:', result.data?.length, 'methods')
        setPaymentMethods(result.data || [])
      }
    } catch (err) {
      console.warn('⚠️ AccountDialog: Payment methods unavailable:', err.message)
      // In local development, payment methods API may not be available
      setPaymentMethods([])
    } finally {
      setIsLoadingPaymentMethods(false)
    }
  }

  // Removed loadMedicationSubscriptions function - no longer needed

  const loadUpcomingInvoice = async () => {
    setIsLoadingUpcomingInvoice(true)
    setUpcomingInvoiceError(null)
    try {
      console.log('🧾 AccountDialog: Loading upcoming invoice...')
      
      // Only try to load upcoming invoice if we have profile data already
      if (!userProfile?.id) {
        console.log('🧾 AccountDialog: No patient profile data available yet, skipping upcoming invoice')
        return
      }
      
      // Use the StripeService to get upcoming invoice
      const module = await import('@joinomu/shared')
      const result = await module.StripeService.getUpcomingInvoice(userProfile.id)
      
      if (result.success) {
        console.log('✅ AccountDialog: Upcoming invoice loaded successfully:', result.data)
        setUpcomingInvoice(result.data)
      } else {
        console.warn('⚠️ AccountDialog: Upcoming invoice not available:', result.error)
        // Don't set error state for API unavailability in local dev or when no upcoming invoice exists
        setUpcomingInvoice(null)
      }
    } catch (err) {
      console.warn('⚠️ AccountDialog: Upcoming invoice unavailable:', err.message)
      // In local development, upcoming invoice API may not be available
      setUpcomingInvoice(null)
    } finally {
      setIsLoadingUpcomingInvoice(false)
    }
  }

  const handleSetDefaultPaymentMethod = async (paymentMethodId: string) => {
    if (!userProfile?.id) return
    
    try {
      console.log('🔍 Setting default payment method:', paymentMethodId)
      const module = await import('@joinomu/shared')
      const result = await module.StripeService.setDefaultPaymentMethod(userProfile.id, paymentMethodId)
      
      if (result.success) {
        showToast({
          title: 'Default Payment Method Updated',
          description: 'Your default payment method has been updated successfully.',
          variant: 'success'
        })
        loadPaymentMethods() // Reload to show updated status
      } else {
        throw new Error(result.error)
      }
    } catch (error) {
      console.error('❌ Set default payment method failed:', error)
      showToast({
        title: 'Update Failed',
        description: error instanceof Error ? error.message : 'Failed to update default payment method.',
        variant: 'error'
      })
    }
  }

  const handleDeletePaymentMethod = async (paymentMethodId: string, method: any) => {
    if (!userProfile?.id) return
    
    // Show confirmation dialog with specific warnings
    let confirmMessage = 'Are you sure you want to delete this payment method?'
    if (method.is_subscription_default) {
      confirmMessage += '\n\nThis payment method is currently used by active subscriptions and cannot be deleted.'
      alert(confirmMessage)
      return
    }
    if (method.is_default) {
      confirmMessage += '\n\nThis is your default payment method. A new default will be set automatically.'
    }
    
    const confirmed = window.confirm(confirmMessage)
    if (!confirmed) return
    
    try {
      console.log('🔍 Deleting payment method:', paymentMethodId)
      const module = await import('@joinomu/shared')
      const result = await module.StripeService.deletePaymentMethod(userProfile.id, paymentMethodId)
      
      if (result.success) {
        showToast({
          title: 'Payment Method Deleted',
          description: 'Payment method has been removed successfully.',
          variant: 'success'
        })
        loadPaymentMethods() // Reload to show updated list
      } else {
        throw new Error(result.error)
      }
    } catch (error) {
      console.error('❌ Delete payment method failed:', error)
      showToast({
        title: 'Delete Failed',
        description: error instanceof Error ? error.message : 'Failed to delete payment method.',
        variant: 'error'
      })
    }
  }

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setHasAccountChanges(true)
  }

  const handleHealthChange = (field: string, value: string | string[]) => {
    const updatedHealthData = { ...healthData, [field]: value }
    
    // Auto-calculate BMI when height or weight changes
    if (field === 'heightFeet' || field === 'heightInches' || field === 'weight') {
      const feet = field === 'heightFeet' ? parseFloat(value as string) || 0 : parseFloat(updatedHealthData.heightFeet) || 0
      const inches = field === 'heightInches' ? parseFloat(value as string) || 0 : parseFloat(updatedHealthData.heightInches) || 0
      const weight = field === 'weight' ? parseFloat(value as string) || 0 : parseFloat(updatedHealthData.weight) || 0
      
      // Calculate total height in inches
      const totalHeightInches = (feet * 12) + inches
      
      // Calculate BMI if we have valid height and weight
      if (totalHeightInches > 0 && weight > 0) {
        const bmi = calculateBMI(weight, totalHeightInches)
        updatedHealthData.bmi = bmi.toFixed(1)
        console.log('🧮 BMI calculated:', { feet, inches, weight, totalHeightInches, bmi: bmi.toFixed(1) })
      } else {
        updatedHealthData.bmi = ''
      }
    }
    
    setHealthData(updatedHealthData)
    setHasHealthChanges(true)
  }

  // Allergy management functions
  const addAllergy = (allergy: string) => {
    if (allergy.trim() && !healthData.allergies.includes(allergy.trim())) {
      const updatedAllergies = [...healthData.allergies, allergy.trim()]
      setHealthData(prev => ({ ...prev, allergies: updatedAllergies }))
      setHasHealthChanges(true)
    }
  }

  const removeAllergy = (index: number) => {
    const updatedAllergies = healthData.allergies.filter((_, i) => i !== index)
    setHealthData(prev => ({ ...prev, allergies: updatedAllergies }))
    setHasHealthChanges(true)
  }

  const handleNewAddressChange = (field: string, value: string | boolean) => {
    setNewAddressData(prev => ({ ...prev, [field]: value }))
    setHasAddressChanges(true)
  }

  const handleSaveNewAddress = async () => {
    if (!newAddressData.streetLine1 || !newAddressData.city || !newAddressData.state || !newAddressData.zipCode) {
      setAddressError('Please fill in all required fields')
      return
    }

    setIsSaving(true)
    setAddressError(null)
    try {
      const auth = await getAuthService()
      const { success, error } = await auth.createPatientAddress({
        streetLine1: newAddressData.streetLine1,
        streetLine2: newAddressData.streetLine2,
        city: newAddressData.city,
        state: newAddressData.state,
        zipCode: newAddressData.zipCode,
        isPrimary: newAddressData.isPrimary
      })

      if (error) {
        setAddressError('Failed to save address')
        console.error('❌ Save address error:', error)
        showToast({
          title: 'Save Failed',
          description: 'Failed to save address. Please try again.',
          variant: 'error'
        })
      } else if (success) {
        // Reset form
        setNewAddressData({
          streetLine1: '',
          streetLine2: '',
          city: '',
          state: '',
          zipCode: '',
          isPrimary: false
        })
        setHasAddressChanges(false)
        
        // Force refresh addresses instead of immediate reload
        setForceRefreshAddresses(prev => prev + 1)
        
        // Notify parent component about address change and wait for it to complete
        if (onAddressChange) {
          await onAddressChange()
        }
        
        // Navigate back to billing section
        setCurrentSection('Billing and Plans')
        
        showToast({
          title: 'Address Saved',
          description: 'Your shipping address has been saved successfully.',
          variant: 'success'
        })
      }
    } catch (err) {
      setAddressError('Failed to save address')
      console.error('❌ Save address exception:', err)
      showToast({
        title: 'Save Failed',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'error'
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteAddress = async (addressId: string) => {
    setIsSaving(true)
    try {
      const auth = await getAuthService()
      const { success, error } = await auth.deletePatientAddress(addressId)

      if (error) {
        console.error('❌ Delete address error:', error)
        showToast({
          title: 'Delete Failed',
          description: 'Failed to delete address. Please try again.',
          variant: 'error'
        })
      } else if (success) {
        // Force refresh addresses instead of immediate reload  
        setForceRefreshAddresses(prev => prev + 1)
        
        // Notify parent component about address change and wait for it to complete
        if (onAddressChange) {
          await onAddressChange()
        }
        
        showToast({
          title: 'Address Deleted',
          description: 'Your shipping address has been deleted successfully.',
          variant: 'success'
        })
      }
    } catch (err) {
      console.error('❌ Delete address exception:', err)
      showToast({
        title: 'Delete Failed',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'error'
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleSetPrimaryAddress = async (addressId: string) => {
    setIsSaving(true)
    try {
      const auth = await getAuthService()
      const { success, error } = await auth.updatePatientAddress(addressId, { isPrimary: true })

      if (error) {
        console.error('❌ Set primary address error:', error)
        showToast({
          title: 'Update Failed',
          description: 'Failed to set primary address. Please try again.',
          variant: 'error'
        })
      } else if (success) {
        // Force refresh addresses instead of immediate reload
        setForceRefreshAddresses(prev => prev + 1)
        
        // Notify parent component about address change
        onAddressChange?.()
        
        showToast({
          title: 'Primary Address Updated',
          description: 'Your primary address has been updated successfully.',
          variant: 'success'
        })
      }
    } catch (err) {
      console.error('❌ Set primary address exception:', err)
      showToast({
        title: 'Update Failed',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'error'
      })
    } finally {
      setIsSaving(false)
    }
  }


  const handleSaveAccount = async () => {
    setIsSaving(true)
    setError(null)
    try {
      const auth = await getAuthService()
      
      // Prepare update data
      const updateData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone
      }

      // Update profile
      const { success, error } = await auth.updatePatientProfile(updateData)
      
      if (error) {
        setError('Failed to save changes')
        console.error('Save error:', error)
        showToast({
          title: 'Save Failed',
          description: 'Failed to update account information. Please try again.',
          variant: 'error'
        })
      } else if (success) {
        setHasAccountChanges(false)
        // Reload profile to get updated data
        await loadUserProfile()
        
        // Show success toast
        showToast({
          title: 'Account Updated',
          description: 'Your account information has been saved successfully.',
          variant: 'success'
        })
      }

      // Handle password update separately if provided
      if (formData.password) {
        const { success: passwordSuccess, error: passwordError } = await auth.updatePassword('', formData.password)
        if (passwordError) {
          setError('Profile saved but password update failed')
          console.error('Password update error:', passwordError)
          showToast({
            title: 'Password Update Failed',
            description: 'Account information saved but password update failed.',
            variant: 'warning'
          })
        } else if (passwordSuccess) {
          setFormData(prev => ({ ...prev, password: '' }))
          showToast({
            title: 'Password Updated',
            description: 'Your password has been changed successfully.',
            variant: 'success'
          })
        }
      }
    } catch (err) {
      setError('Failed to save changes')
      console.error('Save exception:', err)
      showToast({
        title: 'Save Failed',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'error'
      })
    } finally {
      // Brief delay to ensure toast shows before resetting UI state
      setTimeout(() => {
        setIsSaving(false)
      }, 100)
    }
  }

  const handleSaveHealth = async () => {
    setIsSaving(true)
    setError(null)
    try {
      console.log('🏥 HEALTH SAVE: Starting health profile save process')
      const auth = await getAuthService()
      console.log('🏥 HEALTH SAVE: Auth service obtained')
      
      // Prepare health data for update
      const updateData: any = {
        height_feet: healthData.heightFeet ? parseInt(healthData.heightFeet) : null,
        height_inches: healthData.heightInches ? parseInt(healthData.heightInches) : null,
        weight: healthData.weight || null,
        bmi: healthData.bmi ? parseFloat(healthData.bmi) : null,
        allergies: healthData.allergies
      }

      console.log('🏥 HEALTH SAVE: Update data prepared:', updateData)

      // Update patient profile with health data
      console.log('🏥 HEALTH SAVE: Calling updatePatientProfile...')
      const { success, error } = await auth.updatePatientProfile(updateData)
      console.log('🏥 HEALTH SAVE: Update result:', { success, error })
      
      if (error) {
        setError('Failed to save health profile changes')
        console.error('Health save error:', error)
        showToast({
          title: 'Save Failed',
          description: 'Failed to update health profile. Please try again.',
          variant: 'error'
        })
      } else if (success) {
        setHasHealthChanges(false)
        // Reload profile to get updated data
        await loadUserProfile()
        
        // Show success toast
        showToast({
          title: 'Health Profile Updated',
          description: 'Your health information has been saved successfully.',
          variant: 'success'
        })
      }
    } catch (err) {
      setError('Failed to save health profile changes')
      console.error('Health save exception:', err)
      showToast({
        title: 'Save Failed',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'error'
      })
    } finally {
      setTimeout(() => {
        setIsSaving(false)
      }, 100)
    }
  }

  const handleSectionChange = (section: string) => {
    setCurrentSection(section)
    onSectionChange?.(section)
  }

  const renderAccountContent = () => (
    <div className="space-y-6">
      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}
      
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">
          Loading profile data...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input 
                id="firstName" 
                value={formData.firstName}
                onChange={(e) => handleFormChange('firstName', e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input 
                id="lastName" 
                value={formData.lastName}
                onChange={(e) => handleFormChange('lastName', e.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input 
              id="email" 
              type="email" 
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              disabled={isSaving}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input 
              id="phone" 
              type="tel" 
              value={formData.phone}
              onChange={(e) => handleFormChange('phone', e.target.value)}
              disabled={isSaving}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input 
              id="password" 
              type="password" 
              placeholder="••••••••" 
              value={formData.password}
              onChange={(e) => handleFormChange('password', e.target.value)}
              disabled={isSaving}
            />
            <p className="text-sm text-muted-foreground">Leave blank to keep current password</p>
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button 
              variant="outline" 
              disabled={isSaving}
              onClick={() => {
                setFormData({
                  firstName: userProfile?.first_name || '',
                  lastName: userProfile?.last_name || '',
                  email: userProfile?.email || '',
                  phone: userProfile?.phone || '',
                  password: ''
                })
                setHasAccountChanges(false)
                setError(null)
              }}
            >
              Cancel
            </Button>
            <Button 
              disabled={!hasAccountChanges || isSaving}
              onClick={handleSaveAccount}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </>
      )}
    </div>
  )

  const renderHealthProfileContent = () => (
    <div className="space-y-6 px-1">
        {/* Assigned Provider Email Section */}
        <Card className={providerEmail ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"}>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <Label className={`text-sm font-medium ${providerEmail ? "text-blue-900" : "text-gray-700"}`}>
                Assigned Provider Contact
              </Label>
              {isLoadingProviderEmail ? (
                <p className="text-sm text-muted-foreground">Loading provider information...</p>
              ) : providerEmail ? (
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-blue-800">{providerEmail.providerName}</p>
                  <p className="text-sm text-blue-700">{providerEmail.email}</p>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-sm text-gray-600">No provider currently assigned</p>
                  <p className="text-xs text-gray-500">
                    A provider will be assigned when you upgrade to the Medication + Tracking plan
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="height">Height</Label>
            <div className="flex space-x-2">
              <Input 
                id="height-ft" 
                placeholder="6" 
                className="w-20" 
                value={healthData.heightFeet}
                onChange={(e) => handleHealthChange('heightFeet', e.target.value)}
                disabled={isSaving}
              />
              <span className="flex items-center">ft</span>
              <Input 
                id="height-in" 
                placeholder="2" 
                className="w-20" 
                value={healthData.heightInches}
                onChange={(e) => handleHealthChange('heightInches', e.target.value)}
                disabled={isSaving}
              />
              <span className="flex items-center">in</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="weight">Weight (lbs)</Label>
            <Input 
              id="weight" 
              placeholder="180" 
              value={healthData.weight}
              onChange={(e) => handleHealthChange('weight', e.target.value)}
              disabled={isSaving}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="bmi">BMI</Label>
            <Input 
              id="bmi" 
              placeholder="Calculated automatically" 
              value={healthData.bmi}
              disabled={true}
              className="bg-gray-50 text-gray-600"
            />
          </div>
        </div>


        <div className="space-y-2">
          <Label htmlFor="allergies">Allergies</Label>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-3 border border-input rounded-md bg-background">
              {healthData.allergies.map((allergy, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="bg-red-50 text-red-700 hover:bg-red-100"
                >
                  {allergy}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 ml-1 hover:bg-red-200"
                    onClick={() => removeAllergy(index)}
                    disabled={isSaving}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
              {healthData.allergies.length === 0 && (
                <span className="text-muted-foreground text-sm">No allergies added</span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add allergy (e.g., peanuts, shellfish, latex)..."
                disabled={isSaving}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addAllergy(e.currentTarget.value)
                    e.currentTarget.value = ''
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isSaving}
                onClick={(e) => {
                  const input = e.currentTarget.previousElementSibling as HTMLInputElement
                  addAllergy(input.value)
                  input.value = ''
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Add allergies one at a time. This information will be shared with your healthcare provider.
          </p>
        </div>

        <div className="flex justify-end space-x-2">
          <Button 
            variant="outline" 
            disabled={isSaving}
            onClick={() => {
              // Extract health data from intake_data if available, fallback to direct fields
              const intakeData = userProfile?.intake_data || {}
              setHealthData({
                heightFeet: intakeData.height_feet || userProfile?.height_feet || '',
                heightInches: intakeData.height_inches || userProfile?.height_inches || '',
                weight: intakeData.weight || userProfile?.weight || '',
                bmi: intakeData.bmi || userProfile?.bmi || '',
                allergies: Array.isArray(intakeData.allergies) 
                  ? intakeData.allergies 
                  : Array.isArray(userProfile?.allergies) 
                    ? userProfile.allergies 
                    : (intakeData.allergies ? [intakeData.allergies] : userProfile?.allergies ? [userProfile.allergies] : [])
              })
              setHasHealthChanges(false)
              setError(null)
            }}
          >
            Cancel
          </Button>
          <Button 
            disabled={!hasHealthChanges || isSaving}
            onClick={handleSaveHealth}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
    </div>
  )


  const renderBillingContent = () => {
    // Determine plan details - simplified to only show tracking or medication+tracking
    const getPlanDetails = () => {
      if (!userProfile || isLoading) {
        return {
          name: 'Loading...',
          price: 'Loading...',
          hasActivePlan: false,
          buttonText: 'Loading...'
        }
      }

      const subscriptionPlan = userProfile?.subscription_plan
      
      if (!subscriptionPlan) {
        return {
          name: 'No Plan Selected',
          price: 'Choose a plan to get started',
          hasActivePlan: false,
          buttonText: 'View Plans'
        }
      } else if (subscriptionPlan === 'tracking') {
        return {
          name: 'Tracking Only',
          price: '$0/month',
          hasActivePlan: true,
          buttonText: 'Change Plan'
        }
      } else {
        // All other plans (medication, paid plans) are "Medication + Tracking"
        return {
          name: 'Medication + Tracking',
          price: 'Active subscription',
          hasActivePlan: true,
          buttonText: 'Change Plan'
        }
      }
    }

    const planDetails = getPlanDetails()

    return (
      <div className="space-y-6">
        {/* Show loading state if userProfile not yet loaded */}
        {isLoading && (
          <div className="text-center py-8 text-muted-foreground">
            Loading billing information...
          </div>
        )}

        {/* Active Plan */}
        <Card>
          <CardHeader>
            <CardTitle>Active Plan</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Platform Plan - Simplified */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">{planDetails.name}</p>
                <p className="text-sm text-muted-foreground">{planDetails.price}</p>
              </div>
              <div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleSectionChange('Edit Membership')}
                  disabled={isLoading}
                >
                  {planDetails.buttonText}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>


      {/* Account Credits Section */}
      {upcomingInvoice && upcomingInvoice.customerBalance > 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-800">Account Credit</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-green-900">Available Credit Balance</p>
                <p className="text-sm text-green-700">
                  This credit will be automatically applied to your next invoice
                </p>
              </div>
              <p className="text-2xl font-bold text-green-800">
                ${upcomingInvoice.customerBalance.toFixed(2)}
              </p>
            </div>
            {upcomingInvoice.nextInvoiceAmount === 0 && upcomingInvoice.nextBillingDate && (
              <div className="bg-green-100 p-3 rounded-md">
                <p className="text-sm text-green-800 font-medium">
                  🎉 Your next invoice will be $0.00 thanks to your account credit!
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payment Methods */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Methods</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {paymentMethodsError && (
            <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
              {paymentMethodsError}
            </div>
          )}
          
          {isLoadingPaymentMethods ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading payment methods...
            </div>
          ) : paymentMethods.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No payment methods found. Add a payment method to manage billing.
            </div>
          ) : (
            (() => {
              console.log('💳 PAYMENT METHODS DEBUG: Raw payment methods from API:', paymentMethods)
              console.log('💳 PAYMENT METHODS DEBUG: Number of payment methods before deduplication:', paymentMethods.length)
              
              // Enhanced debug logging to show card details
              paymentMethods.forEach((method, index) => {
                console.log(`💳 PAYMENT METHOD ${index + 1} DETAILS:`, {
                  id: method.id,
                  brand: method.card?.brand,
                  last4: method.card?.last4,
                  exp_month: method.card?.exp_month,
                  exp_year: method.card?.exp_year,
                  is_default: method.is_default,
                  is_subscription_default: method.is_subscription_default,
                  used_by_subscriptions_count: method.used_by_subscriptions?.length || 0,
                  created: method.created
                })
              })
              
              // Deduplicate payment methods by card details (brand + last4) to avoid showing the same physical card multiple times
              const uniquePaymentMethods = paymentMethods.reduce((unique, method) => {
                const cardKey = `${method.card?.brand}-${method.card?.last4}`
                const existingMethod = unique.find(m => `${m.card?.brand}-${m.card?.last4}` === cardKey)
                if (existingMethod) {
                  console.log('💳 DUPLICATE CARD FOUND: Merging payment method', method.id, 'with existing', existingMethod.id, `(${cardKey})`)
                  // Merge subscription information if payment method already exists
                  if (method.used_by_subscriptions && method.used_by_subscriptions.length > 0) {
                    existingMethod.used_by_subscriptions = [
                      ...(existingMethod.used_by_subscriptions || []),
                      ...method.used_by_subscriptions
                    ]
                  }
                  // Keep the highest priority status flags
                  if (method.is_default) existingMethod.is_default = true
                  if (method.is_subscription_default) existingMethod.is_subscription_default = true
                  // Keep the most recent payment method ID (higher timestamp or newer created date)
                  const existingCreated = new Date(existingMethod.created).getTime()
                  const methodCreated = new Date(method.created).getTime()
                  if (methodCreated > existingCreated) {
                    console.log('💳 Using newer payment method ID:', method.id, 'instead of', existingMethod.id)
                    existingMethod.id = method.id
                  }
                } else {
                  console.log('💳 NEW CARD: Adding', method.id, `(${cardKey})`)
                  unique.push({ ...method })
                }
                return unique
              }, [])

              console.log('💳 PAYMENT METHODS DEBUG: Number of payment methods after deduplication:', uniquePaymentMethods.length)
              console.log('💳 PAYMENT METHODS DEBUG: Unique payment methods:', uniquePaymentMethods)

              return uniquePaymentMethods.map((method) => (
                <div key={method.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium">
                        {method.card.brand.toUpperCase()} •••• •••• •••• {method.card.last4}
                      </p>
                      {method.is_default && (
                        <Badge variant="default">Default</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Expires {method.card.exp_month.toString().padStart(2, '0')}/{method.card.exp_year.toString().slice(-2)}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleSetDefaultPaymentMethod(method.id)}
                      disabled={method.is_default}
                    >
                      {method.is_default ? 'Default' : 'Set Default'}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0"
                      onClick={() => handleDeletePaymentMethod(method.id, method)}
                      disabled={method.is_subscription_default}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            })()
          )}
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => setAddPaymentMethodOpen(true)}
          >
            Add Payment Method
          </Button>
        </CardContent>
      </Card>

      {/* Shipping Information */}
      <Card>
        <CardHeader>
          <CardTitle>Shipping Addresses</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {addressError && (
            <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
              {addressError}
            </div>
          )}
          
          {isLoadingAddresses ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading addresses...
            </div>
          ) : patientAddresses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No shipping addresses found. Add your first address to get started.
            </div>
          ) : (
            patientAddresses.map((address) => (
              <Card key={address.id} className={address.is_primary ? 'border-primary' : ''}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium">{address.street_line_1}</p>
                    {address.is_primary ? (
                      <span className="text-xs font-medium text-primary">Primary</span>
                    ) : (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-xs"
                        onClick={() => handleSetPrimaryAddress(address.id)}
                        disabled={isSaving}
                      >
                        Set as Primary
                      </Button>
                    )}
                  </div>
                  {address.street_line_2 && (
                    <p className="text-sm text-muted-foreground">{address.street_line_2}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {address.city}, {address.state} {address.zip_code}
                  </p>
                  <div className="flex space-x-2 mt-3">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0"
                      onClick={() => handleDeleteAddress(address.id)}
                      disabled={isSaving}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
          
          <Button variant="outline" className="w-full" onClick={() => handleSectionChange('Add Shipping Address')}>Add New Shipping Address</Button>
        </CardContent>
      </Card>

      {/* All Payments */}
      <Card>
        <CardHeader>
          <CardTitle>All Payments</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {paymentHistoryError && (
            <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
              {paymentHistoryError}
            </div>
          )}
          
          {isLoadingPaymentHistory ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading payment history...
            </div>
          ) : paymentHistory.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No payments found. Payments will appear here after your first transaction.
            </div>
          ) : (
            <div className="space-y-3">
              {paymentHistory.slice(0, 5).map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{payment.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(payment.created).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        payment.status === 'succeeded' || payment.status === 'paid' 
                          ? 'bg-green-100 text-green-800' 
                          : payment.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {payment.status === 'succeeded' ? 'Paid' : payment.status}
                      </span>
                      {(payment.receipt_url || payment.hosted_invoice_url) && (
                        <a 
                          href={payment.receipt_url || payment.hosted_invoice_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          View Receipt
                        </a>
                      )}
                    </div>
                  </div>
                  <p className="font-semibold">${payment.amount.toFixed(2)}</p>
                </div>
              ))}
              {paymentHistory.length > 5 && (
                <div className="text-center py-2">
                  <span className="text-sm text-muted-foreground">
                    Showing recent 5 of {paymentHistory.length} payments
                  </span>
                </div>
              )}
            </div>
          )}
          
          {/* Credit notification for All Payments section */}
          {upcomingInvoice && upcomingInvoice.customerBalance > 0 && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-muted-foreground">
                You have a credit of ${upcomingInvoice.customerBalance.toFixed(2)} which will be applied to your next payment.
              </p>
            </div>
          )}
          
          <Button 
            variant="outline" 
            className="w-full mt-4"
            onClick={() => onSupportClick?.()}
          >
            Cancel a Payment
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

  const renderPreferencesContent = () => (
    <MetricPreferencesProvider>
      <PreferencesWithProvider 
        currentSection={currentSection}
        isSaving={isSaving}
        hasPreferencesChanges={hasPreferencesChanges}
        setHasPreferencesChanges={setHasPreferencesChanges}
        setError={setError}
        setIsSaving={setIsSaving}
        handleCancelPreferences={() => setHasPreferencesChanges(false)}
        handleSavePreferences={() => setHasPreferencesChanges(false)}
      />
    </MetricPreferencesProvider>
  )

  const renderEditMembershipContent = () => {
    const currentPlan = userProfile?.subscription_plan

    return (
      <div className="space-y-6 pb-8">
        {/* Pricing Cards */}
        <div className="flex flex-col md:flex-row gap-4 max-w-4xl mx-auto mt-12">
          {/* Tracking Only Plan */}
          <Card className={`flex-1 ${currentPlan === 'tracking' ? 'border-primary' : ''}`}>
            <CardContent className="p-8 space-y-8">
              <div className="space-y-6">
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-foreground">Tracking only</h3>
                </div>
                <div className="flex items-end gap-1">
                  <span className="text-4xl font-semibold text-foreground">$0</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                {currentPlan === 'tracking' ? (
                  <p className="text-center text-sm text-primary font-medium py-2">Current Plan</p>
                ) : (
                  <Button variant="secondary" className="w-full">Select plan</Button>
                )}
              </div>
            
            <div className="space-y-4">
              <p className="text-sm font-medium text-foreground">What's included:</p>
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 flex items-start gap-3">
                  <p className="text-sm text-muted-foreground flex-1">
                    Comprehensive medication and health tracking to optimize your results
                  </p>
                  <Info className="h-4 w-4 text-muted-foreground opacity-70 mt-0.5 flex-shrink-0" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Medication + Tracking Plan */}
        <Card className={`flex-1 ${currentPlan === 'medication' ? 'border-primary' : ''}`}>
          <CardContent className="p-8 space-y-8">
            <div className="space-y-6">
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground">Medication + Tracking</h3>
              </div>
              <div className="flex items-end gap-1">
                <span className="text-4xl font-semibold text-foreground">$79</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              {(currentPlan === 'medication' || currentPlan === '79 usd/month') ? (
                <p className="text-center text-sm text-primary font-medium py-2">Current Plan</p>
              ) : (
                <Button className="w-full">Select plan</Button>
              )}
            </div>
            
            <div className="space-y-4">
              <p className="text-sm font-medium text-foreground">Everything in Tracking only, plus:</p>
              
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 flex items-start gap-3">
                  <p className="text-sm text-muted-foreground flex-1">
                    Unlimited access to a licensed JoinOmu physician with treatment level expertise
                  </p>
                  <Info className="h-4 w-4 text-muted-foreground opacity-70 mt-0.5 flex-shrink-0" />
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 flex items-start gap-3">
                  <p className="text-sm text-muted-foreground flex-1">
                    Access to prescription medications
                  </p>
                  <Info className="h-4 w-4 text-muted-foreground opacity-70 mt-0.5 flex-shrink-0" />
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 flex items-start gap-3">
                  <p className="text-sm text-muted-foreground flex-1">
                    Regular check ins with your provider
                  </p>
                  <Info className="h-4 w-4 text-muted-foreground opacity-70 mt-0.5 flex-shrink-0" />
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 flex items-start gap-3">
                  <p className="text-sm text-muted-foreground flex-1">
                    On demand dosage adjustments
                  </p>
                  <Info className="h-4 w-4 text-muted-foreground opacity-70 mt-0.5 flex-shrink-0" />
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 flex items-start gap-3">
                  <p className="text-sm text-muted-foreground flex-1">
                    Access to 24/7 messaging with your care team
                  </p>
                  <Info className="h-4 w-4 text-muted-foreground opacity-70 mt-0.5 flex-shrink-0" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Description */}
      <div className="text-center">
        <p className="text-muted-foreground text-sm leading-6 max-w-2xl mx-auto">
          Either plan gives access to comprehensive medication and health tracking to optimize your results regardless of if you're seeing a JoinOmu provider.
        </p>
      </div>
    </div>
    )
  }


  const renderAddShippingAddressContent = () => {
    return (
      <div className="space-y-6 pb-8">
        {addressError && (
          <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
            {addressError}
          </div>
        )}
        
        <div className="grid grid-cols-1 gap-6">
          <div className="space-y-2">
            <Label htmlFor="newStreetLine1">Street Address</Label>
            <Input 
              id="newStreetLine1" 
              placeholder="Enter street address" 
              value={newAddressData.streetLine1}
              onChange={(e) => handleNewAddressChange('streetLine1', e.target.value)}
              disabled={isSaving}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="newStreetLine2">Apartment, suite, etc. (optional)</Label>
            <Input 
              id="newStreetLine2" 
              placeholder="Apt, suite, unit, etc." 
              value={newAddressData.streetLine2}
              onChange={(e) => handleNewAddressChange('streetLine2', e.target.value)}
              disabled={isSaving}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="newCity">City</Label>
              <Input 
                id="newCity" 
                placeholder="Enter city" 
                value={newAddressData.city}
                onChange={(e) => handleNewAddressChange('city', e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newState">State</Label>
              <Select 
                value={newAddressData.state} 
                onValueChange={(value) => handleNewAddressChange('state', value)}
                disabled={isSaving}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="al">Alabama</SelectItem>
                  <SelectItem value="ak">Alaska</SelectItem>
                  <SelectItem value="az">Arizona</SelectItem>
                  <SelectItem value="ar">Arkansas</SelectItem>
                  <SelectItem value="ca">California</SelectItem>
                  <SelectItem value="co">Colorado</SelectItem>
                  <SelectItem value="ct">Connecticut</SelectItem>
                  <SelectItem value="de">Delaware</SelectItem>
                  <SelectItem value="fl">Florida</SelectItem>
                  <SelectItem value="ga">Georgia</SelectItem>
                  <SelectItem value="hi">Hawaii</SelectItem>
                  <SelectItem value="id">Idaho</SelectItem>
                  <SelectItem value="il">Illinois</SelectItem>
                  <SelectItem value="in">Indiana</SelectItem>
                  <SelectItem value="ia">Iowa</SelectItem>
                  <SelectItem value="ks">Kansas</SelectItem>
                  <SelectItem value="ky">Kentucky</SelectItem>
                  <SelectItem value="la">Louisiana</SelectItem>
                  <SelectItem value="me">Maine</SelectItem>
                  <SelectItem value="md">Maryland</SelectItem>
                  <SelectItem value="ma">Massachusetts</SelectItem>
                  <SelectItem value="mi">Michigan</SelectItem>
                  <SelectItem value="mn">Minnesota</SelectItem>
                  <SelectItem value="ms">Mississippi</SelectItem>
                  <SelectItem value="mo">Missouri</SelectItem>
                  <SelectItem value="mt">Montana</SelectItem>
                  <SelectItem value="ne">Nebraska</SelectItem>
                  <SelectItem value="nv">Nevada</SelectItem>
                  <SelectItem value="nh">New Hampshire</SelectItem>
                  <SelectItem value="nj">New Jersey</SelectItem>
                  <SelectItem value="nm">New Mexico</SelectItem>
                  <SelectItem value="ny">New York</SelectItem>
                  <SelectItem value="nc">North Carolina</SelectItem>
                  <SelectItem value="nd">North Dakota</SelectItem>
                  <SelectItem value="oh">Ohio</SelectItem>
                  <SelectItem value="ok">Oklahoma</SelectItem>
                  <SelectItem value="or">Oregon</SelectItem>
                  <SelectItem value="pa">Pennsylvania</SelectItem>
                  <SelectItem value="ri">Rhode Island</SelectItem>
                  <SelectItem value="sc">South Carolina</SelectItem>
                  <SelectItem value="sd">South Dakota</SelectItem>
                  <SelectItem value="tn">Tennessee</SelectItem>
                  <SelectItem value="tx">Texas</SelectItem>
                  <SelectItem value="ut">Utah</SelectItem>
                  <SelectItem value="vt">Vermont</SelectItem>
                  <SelectItem value="va">Virginia</SelectItem>
                  <SelectItem value="wa">Washington</SelectItem>
                  <SelectItem value="wv">West Virginia</SelectItem>
                  <SelectItem value="wi">Wisconsin</SelectItem>
                  <SelectItem value="wy">Wyoming</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="newZipCode">ZIP Code</Label>
              <Input 
                id="newZipCode" 
                placeholder="Enter ZIP code" 
                value={newAddressData.zipCode}
                onChange={(e) => handleNewAddressChange('zipCode', e.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox 
              id="setNewPrimary" 
              checked={newAddressData.isPrimary}
              onCheckedChange={(checked) => handleNewAddressChange('isPrimary', checked)}
              disabled={isSaving}
            />
            <Label htmlFor="setNewPrimary">Set as primary address</Label>
          </div>
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button 
            variant="outline"
            disabled={isSaving}
            onClick={() => {
              setCurrentSection('Billing and Plans')
              setNewAddressData({
                streetLine1: '',
                streetLine2: '',
                city: '',
                state: '',
                zipCode: '',
                isPrimary: false
              })
              setHasAddressChanges(false)
              setAddressError(null)
            }}
          >
            Cancel
          </Button>
          <Button 
            disabled={!hasAddressChanges || isSaving}
            onClick={handleSaveNewAddress}
          >
            {isSaving ? 'Saving...' : 'Add Address'}
          </Button>
        </div>
      </div>
    )
  }

  const renderSectionContent = () => {
    switch (currentSection) {
      case 'Account':
        return renderAccountContent()
      case 'Health Profile':
        return renderHealthProfileContent()
      case 'Billing and Plans':
        return renderBillingContent()
      case 'Preferences':
        return renderPreferencesContent()
      case 'Edit Membership':
        return renderEditMembershipContent()
      case 'Add Shipping Address':
        return renderAddShippingAddressContent()
      default:
        return null
    }
  }

  return (
    <>
      <ExpandableDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Account Settings"
        description="Manage your account settings, health profile, billing, and preferences"
        sidebarItems={sidebarItems}
        activeSection={currentSection}
        onSectionChange={handleSectionChange}
        hideExpandButton={false}
        defaultExpanded={true}
        breadcrumbParent={currentSection === 'Edit Membership' || currentSection === 'Add Shipping Address' ? 'Billing and Plans' : undefined}
        onBreadcrumbParentClick={currentSection === 'Edit Membership' || currentSection === 'Add Shipping Address' ? () => handleSectionChange('Billing and Plans') : undefined}
      >
        {renderSectionContent()}
      </ExpandableDialog>
      
      <AddPaymentMethodDialog
        open={addPaymentMethodOpen}
        onOpenChange={setAddPaymentMethodOpen}
        onSuccess={() => {
          loadPaymentMethods();
          showToast({
            title: 'Payment Method Added',
            description: 'New payment method has been added successfully.',
            variant: 'success'
          });
        }}
        patientId={userProfile?.id}
      />
    </>
  )
}